import { Link } from "react-router-dom";
import { Mail, LogIn, MapPin, WifiOff, Trash2 } from "lucide-react";

const TOPICS = [
  {
    Icon: LogIn,
    title: "Signing in",
    body: "Use the username and password provided by your organization's administrator. If you've forgotten your credentials, contact your administrator or email us.",
  },
  {
    Icon: MapPin,
    title: "Location and photos",
    body: "The app uses your device's GPS to tag field photos. If location isn't working, check that location permission is enabled for LocateOS in your device settings.",
  },
  {
    Icon: WifiOff,
    title: "Offline use",
    body: "The app works offline and automatically syncs your tickets, responses, and photos when your connection returns.",
  },
  {
    Icon: Trash2,
    title: "Account deletion",
    body: "You can delete your account from Profile → Delete Account inside the app.",
  },
];

export default function Support() {
  return (
    <section className="section-y relative">
      <div className="absolute inset-0 dot-grid opacity-15 pointer-events-none" />
      <div className="container-x relative max-w-3xl">
        <div className="reveal">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#F97316] mb-3">Support</div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-white tracking-tight">
            LocateOS Support
          </h1>
          <p className="mt-5 text-lg text-[#94A3B8] leading-relaxed">
            Need help with the LocateOS app? We're here to assist field technicians, supervisors, and administrators.
          </p>
        </div>

        {/* Contact card */}
        <div className="reveal mt-10 rounded-2xl glass-strong border-l-4 border-l-[#F97316] p-6 md:p-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-11 w-11 rounded-lg bg-[#F97316]/15 border border-[#F97316]/30 grid place-items-center">
              <Mail size={20} className="text-[#F97316]" />
            </div>
            <div className="font-display font-bold text-xl text-white">Contact</div>
          </div>
          <p className="text-[#94A3B8] leading-relaxed">
            For all support questions, account help, bug reports, or feedback, email us at:
          </p>
          <a
            href="mailto:info@locateos.com"
            data-testid="support-email-link"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#F97316] hover:bg-[#ea580c] text-white font-semibold px-5 py-3 transition-colors"
          >
            <Mail size={16} />
            info@locateos.com
          </a>
          <p className="mt-4 text-sm text-[#94A3B8]">We aim to respond within one business day.</p>
        </div>

        {/* Common topics */}
        <div className="reveal mt-12">
          <h2 className="font-display font-bold text-2xl text-white mb-4">Common Topics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TOPICS.map(({ Icon, title, body }) => (
              <div key={title} className="rounded-2xl glass p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-9 w-9 rounded-lg bg-[#FB923C]/15 border border-[#FB923C]/30 grid place-items-center">
                    <Icon size={16} className="text-[#FDBA74]" />
                  </div>
                  <div className="font-display font-bold text-white">{title}</div>
                </div>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* About */}
        <div className="reveal mt-12 rounded-2xl glass p-6 md:p-8">
          <h2 className="font-display font-bold text-2xl text-white mb-3">About LocateOS</h2>
          <p className="text-[#94A3B8] leading-relaxed">
            LocateOS is a ticket-management platform for utility-locating operations, used by field technicians to manage locate tickets, submit responses, and capture GPS-stamped site photos.
          </p>
          <div className="mt-5 border-t border-white/5 pt-4 text-sm">
            <div className="text-white font-semibold">LocateOS Inc.</div>
            <div className="text-[#94A3B8]">Email: <a className="text-[#F97316] hover:underline" href="mailto:info@locateos.com">info@locateos.com</a></div>
            <div className="text-[#94A3B8]">Website: <a className="text-[#F97316] hover:underline" href="https://locateos.com">https://locateos.com</a></div>
          </div>
        </div>

        <div className="reveal mt-14 pt-8 border-t border-white/5 flex items-center justify-between text-sm">
          <Link to="/" className="text-[#94A3B8] hover:text-white">← Back to home</Link>
          <Link to="/privacy" className="text-[#F97316] hover:underline">Privacy Policy →</Link>
        </div>
      </div>
    </section>
  );
}
